import React, { useState, useEffect } from 'react';

export default function PrescriptionLoader({ open = true, onClose }) {
  const [progress, setProgress] = useState(0);
  
  const processingSteps = [
    { label: "Reading Prescription" },
    { label: "Analyzing Handwriting" },
    { label: "Processing Medication" },
    { label: "Verifying Results" }
  ];

  // Calculate active step based on progress
  const activeStep = progress < 25 ? 0 : progress < 50 ? 1 : progress < 75 ? 2 : 3;

  // Simulate AI processing steps
  useEffect(() => {
    if (!open) return;
    
    const timer = setInterval(() => {
      setProgress((prevProgress) => {
        const newProgress = prevProgress + 1;
        if (newProgress >= 100) {
          clearInterval(timer);
          // Call the onClose callback after 1 second
          setTimeout(() => onClose?.(), 1000);
          return 100;
        }
        return newProgress;
      });
    }, 300);

    return () => clearInterval(timer);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="dialog">
      <div className="dialog-content">
        <h3 className="title">Analyzing Prescription</h3>
        
        {/* Progress bar */}
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progress}%` }} 
            />
          </div>
          <div className="progress-text">{progress}%</div>
        </div>
        
        {/* Processing steps */}
        <div className="steps-list">
          {processingSteps.map((step, index) => (
            <div
              key={index}
              className={`step-item ${activeStep === index ? 'active' : ''} ${activeStep > index ? 'completed' : ''}`}
            >
              <div className="step-content">
                <div className="step-label">{step.label}</div>
                <div className="step-status">
                  {activeStep === index ? "Processing..." : 
                   activeStep > index ? "Complete" : "Waiting..."}
                </div>
              </div>
              {activeStep === index && <div className="loader" />}
              {activeStep > index && <div className="check-mark">✓</div>}
            </div>
          ))}
        </div>
        
        {/* Simplified processing visualization */}
        <div className="processing-visual">
          {[...Array(5)].map((_, i) => (
            <div 
              key={i}
              className="processing-bar"
              style={{
                animationDelay: `${i * 0.1}s`
              }}
            />
          ))}
        </div>
      </div>

      <style jsx>{`
        .dialog {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: transparent;
          z-index: 1000;
        }
        
        .dialog-content {
          background: white;
          border-radius: 8px;
          padding: 24px;
          width: 90%;
          max-width: 400px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        
        .title {
          text-align: center;
          margin-bottom: 16px;
          font-weight: 500;
        }
        
        .progress-container {
          width: 100%;
          margin-bottom: 20px;
        }
        
        .progress-bar {
          height: 8px;
          background-color: #e0e0e0;
          border-radius: 4px;
          overflow: hidden;
        }
        
        .progress-fill {
          height: 100%;
          background-color: #1976d2;
          transition: width 0.3s ease;
        }
        
        .progress-text {
          text-align: right;
          font-size: 14px;
          color: #666;
          margin-top: 4px;
        }
        
        .steps-list {
          margin-bottom: 16px;
        }
        
        .step-item {
          padding: 12px;
          margin-bottom: 8px;
          border-radius: 4px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          opacity: 0.7;
          background-color: #f5f5f5;
        }
        
        .step-item.active {
          border-left: 4px solid #1976d2;
          opacity: 1;
          background-color: #e3f2fd;
        }
        
        .step-item.completed {
          opacity: 1;
        }
        
        .step-label {
          font-weight: 500;
        }
        
        .step-status {
          font-size: 14px;
          color: #666;
        }
        
        .loader {
          width: 18px;
          height: 18px;
          border: 2px solid #1976d2;
          border-radius: 50%;
          border-top-color: transparent;
          animation: spin 1s linear infinite;
        }
        
        .check-mark {
          color: #4caf50;
          font-weight: bold;
        }
        
        .processing-visual {
          display: flex;
          justify-content: center;
          gap: 4px;
          margin-top: 20px;
          padding: 16px;
          background-color: #f5f5f5;
          border-radius: 8px;
        }
        
        .processing-bar {
          width: 3px;
          height: 20px;
          background-color: #1976d2;
          border-radius: 2px 2px 0 0;
          animation: pulse 1.5s infinite ease-in-out;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { height: 20px; }
          50% { height: 30px; }
        }
      `}</style>
    </div>
  );
}